// "What has this person written inside a lesson."
//
// Nested course -> lesson -> question, not a flat recency feed. A feed loses
// the question that asked for the answer, and the question is most of the
// meaning; recency lives in the card header instead. The most recently answered
// course opens by default so the common case needs no click.
//
// Ordering is server-owned (course -> lesson.position -> question.position) and
// is never re-sorted here. No search or filter: one person's answers inside
// their assigned courses are tens of rows, not thousands.
import { useState } from "react"
import { BookOpen, ChevronDown, ChevronRight, EyeOff, MessageSquare } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// `answeredAt` is updated_at: the member write path upserts in place, so an
// edited answer's created_at dates a draft that no longer exists.
function lastAnsweredAt(courses) {
  const stamps = courses.flatMap((course) =>
    course.lessons.flatMap((lesson) =>
      lesson.questions.map((q) => q.answeredAt).filter(Boolean)
    )
  )
  if (stamps.length === 0) return null
  return stamps.reduce((latest, at) => (at > latest ? at : latest))
}

function QuestionRow({ question }) {
  const answered = !!question.responseText

  return (
    <li className="py-2 border-t border-zinc-100 first:border-t-0">
      <p className="text-xs text-zinc-500">{question.text}</p>
      {answered ? (
        <>
          <p className="text-sm text-zinc-800 whitespace-pre-line mt-1">
            {question.responseText}
          </p>
          {question.answeredAt && (
            <p className="text-xs text-zinc-400 mt-1">
              Answered {formatDate(question.answeredAt)}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-zinc-400 italic mt-1">Not answered yet</p>
      )}
    </li>
  )
}

function LessonBlock({ lesson }) {
  return (
    <div className="rounded-md bg-zinc-50 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
          {lesson.title}
        </span>
        {lesson.coachOnly && (
          <Badge variant="outline" className="border-amber-500 text-amber-600 shrink-0">
            <EyeOff className="h-3 w-3 mr-1" />
            Coach
          </Badge>
        )}
        <span className="text-xs text-zinc-500 tabular-nums shrink-0">
          {lesson.answeredCount} of {lesson.questionCount} answered
        </span>
      </div>
      <ul className="mt-1">
        {lesson.questions.map((question) => (
          <QuestionRow key={question.id} question={question} />
        ))}
      </ul>
    </div>
  )
}

// Same collapse as CourseHistoryCard on this page: useState + aria-expanded +
// a chevron. No accordion primitive — neither exists in components/ui, and one
// card is not a reason to diverge from the page it sits on.
function CourseBlock({ course, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen)
  const answered = course.lessons.reduce((sum, l) => sum + l.answeredCount, 0)

  return (
    <div className="rounded-md border border-zinc-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" />
        )}
        <MessageSquare className="h-4 w-4 text-kf-red shrink-0" />
        <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
          {course.title}
        </span>
        <span className="text-xs text-zinc-500 tabular-nums shrink-0">
          {answered} {answered === 1 ? "answer" : "answers"}
        </span>
      </button>
      {open && (
        <div className="border-t border-zinc-100 px-3 py-2 space-y-2">
          {course.lessons.map((lesson) => (
            <LessonBlock key={lesson.id} lesson={lesson} />
          ))}
        </div>
      )}
    </div>
  )
}

export function LessonAnswersCard({ answers, memberName }) {
  const assigned = answers?.assigned || []
  const elsewhere = answers?.elsewhere || []
  const lastAt = lastAnsweredAt([...assigned, ...elsewhere])

  return (
    <section className="mt-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Lesson answers
            {lastAt && (
              <span className="text-sm font-normal text-zinc-500">
                last answered {formatDate(lastAt)}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {assigned.length === 0 && elsewhere.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">
              {memberName} has not answered any lesson questions yet.
            </p>
          ) : (
            <>
              {assigned.map((course, i) => (
                <CourseBlock key={course.id} course={course} defaultOpen={i === 0} />
              ))}

              {elsewhere.length > 0 && (
                <div className="pt-2 space-y-3">
                  {/* Mirrors CourseHistoryCard's `elsewhere` bucket: work this
                      person did in a course nobody in your groups is assigning
                      right now. */}
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-400">
                    Answered elsewhere
                  </p>
                  {elsewhere.map((course) => (
                    <CourseBlock key={course.id} course={course} defaultOpen={false} />
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
